import { useState, useEffect, useCallback } from "react";
import { Button } from "@app/components/shared/Button";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import {
  getExecutionDiagnostics,
  getDiagnosticsDetail,
  executeDiagnosticsAction,
  type DiagnosticsRow,
  type DiagnosticsCounters,
  type DiagnosticsDetailResponse,
  type DiagnosticsActionRequest,
} from "@app/services/adminExecutionDiagnosticsApi";

type Severity = "normal" | "info" | "warning" | "critical";

const SEVERITY_COLORS: Record<Severity, string> = {
  normal: "#6b7280",
  info: "#3b82f6",
  warning: "#f59e0b",
  critical: "#ef4444",
};

const SEVERITY_LABELS: Record<Severity, string> = {
  normal: "Normal",
  info: "Info",
  warning: "Warning",
  critical: "Critical",
};

const ISSUE_COLORS: Record<string, string> = {
  "DB-only blocker": "#ef4444",
  "TradeLog reopened": "#ef4444",
  "Queue blocked without visible position": "#ef4444",
  "Redis ghost": "#f59e0b",
  "Synthetic history row": "#f59e0b",
  "Duplicate TradeLogs": "#f59e0b",
  "Missing history row": "#f59e0b",
  "Zero exit price": "#f59e0b",
};

const ACTION_LABELS: Record<string, string> = {
  clear_redis_ghost: "Clear Redis Ghost",
  mark_tradelog_final: "Mark TradeLog Final",
  recheck_block_reason: "Recheck Block Reason",
  refresh_sources: "Refresh Sources",
  rebuild_history_row: "Rebuild History",
  sync_from_exchange: "Sync from Exchange",
};

function formatPrice(price: number | null | undefined): string {
  if (price == null) return "—";
  return price.toFixed(6);
}

function IssueBadge({ label, severity }: { label: string; severity: string }) {
  const color = ISSUE_COLORS[label] || SEVERITY_COLORS[severity as Severity] || "#6b7280";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 6px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: color + "22",
        color,
        border: `1px solid ${color}44`,
        marginRight: 4,
        marginBottom: 2,
      }}
    >
      {label}
    </span>
  );
}

export function ExecutionDiagnosticsTab(): JSX.Element {
  const [data, setData] = useState<DiagnosticsRow[]>([]);
  const [counters, setCounters] = useState<DiagnosticsCounters | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mismatchOnly, setMismatchOnly] = useState(true);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [symbolFilter, setSymbolFilter] = useState("");

  const [detailModal, setDetailModal] = useState<DiagnosticsDetailResponse | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<string>("");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getExecutionDiagnostics({
        symbol: symbolFilter || undefined,
        mismatch_only: mismatchOnly,
        blocked_only: blockedOnly,
      });
      setData(result.rows);
      setCounters(result.counters);
    } catch (err: any) {
      setError(err.message || "Failed to load diagnostics");
    } finally {
      setIsLoading(false);
    }
  }, [symbolFilter, mismatchOnly, blockedOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInspect = async (row: DiagnosticsRow) => {
    try {
      const detail = await getDiagnosticsDetail(row.symbol, row.account_id);
      setDetailModal(detail);
    } catch (err: any) {
      setError(err.message || "Failed to load detail");
    }
  };

  const handleAction = async (row: DiagnosticsRow, action: string) => {
    if (actionLoading) return;

    const confirmed = window.confirm(
      `Execute "${ACTION_LABELS[action] || action}" on ${row.symbol}?\n\nReason: ${row.issues.map((i: any) => i.label).join(", ")}`
    );
    if (!confirmed) return;

    setActionLoading(action);
    setActionResult("");
    try {
      const request: DiagnosticsActionRequest = {
        action,
        symbol: row.symbol,
        account_id: row.account_id,
        position_id: row.position_id,
        reason: `admin_fix: ${row.issues.map((i: any) => i.label).join(", ")}`,
      };
      const result = await executeDiagnosticsAction(request);
      const msg = result.success
        ? `Success: ${result.message}`
        : `Failed: ${result.message}`;
      setActionResult(msg);

      setTimeout(() => setActionResult(""), 4000);
      await fetchData();
    } catch (err: any) {
      setActionResult(`Error: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkCleanup = async () => {
    const confirmed = window.confirm(
      "Bulk Cleanup History\n\nThis will:\n" +
        "- Keep the best record for each symbol+account\n" +
        "- Mark duplicate rows as admin_bulk_removed\n" +
        "- Skip all currently open/active positions\n\n" +
        "Continue?"
    );
    if (!confirmed) return;

    setActionLoading("bulk_cleanup_history");
    setActionResult("");
    try {
      const request: DiagnosticsActionRequest = {
        action: "bulk_cleanup_history",
        symbol: "*",
        account_id: "*",
        position_id: "*",
        reason: "admin_bulk_cleanup: deduplicate and finalize historical artifacts",
      };
      const result = await executeDiagnosticsAction(request);
      const msg = result.success
        ? `Success: ${result.message}`
        : `Failed: ${result.message}`;
      setActionResult(msg);
      setTimeout(() => setActionResult(""), 6000);
      await fetchData();
    } catch (err: any) {
      setActionResult(`Error: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const criticalCount = counters?.critical_blockers || 0;
  const mismatchCount = counters?.mismatches || 0;

  return (
    <div style={{ padding: "0 8px" }}>
      <SectionHeader title="Execution Diagnostics" />

      {actionResult && (
        <div
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            marginBottom: 12,
            backgroundColor: actionResult.startsWith("Success")
              ? "#10b98122"
              : "#ef444422",
            color: actionResult.startsWith("Success") ? "#10b981" : "#ef4444",
            fontSize: 13,
            fontWeight: 500,
            border: `1px solid ${actionResult.startsWith("Success") ? "#10b98144" : "#ef444444"}`,
          }}
        >
          {actionResult}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <CounterBox
          label="Redis Active"
          value={counters?.redis_active ?? "—"}
          color="#3b82f6"
        />
        <CounterBox
          label="UserPositions"
          value={counters?.userposition_open ?? "—"}
          color="#8b5cf6"
        />
        <CounterBox
          label="TradeLog Open"
          value={counters?.tradelog_open ?? "—"}
          color="#f59e0b"
        />
        <CounterBox
          label="Queue Blocked"
          value={counters?.queue_blocked ?? "—"}
          color="#ef4444"
        />
        <CounterBox
          label="Mismatches"
          value={mismatchCount}
          color={mismatchCount > 0 ? "#ef4444" : "#10b981"}
        />
        <CounterBox
          label="Critical"
          value={criticalCount}
          color={criticalCount > 0 ? "#ef4444" : "#10b981"}
        />
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={!mismatchOnly}
            onChange={(e) => setMismatchOnly(!e.target.checked)}
          />
          Show all (including clean)
        </label>
        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={blockedOnly}
            onChange={(e) => setBlockedOnly(e.target.checked)}
          />
          Blocked only
        </label>
        <input
          type="text"
          placeholder="Filter by symbol..."
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value)}
          style={{
            padding: "4px 8px",
            fontSize: 13,
            borderRadius: 4,
            border: "1px solid #374151",
            backgroundColor: "#1f2937",
            color: "#f3f4f6",
            width: 160,
          }}
        />
        <Button onClick={fetchData} disabled={isLoading}>
          Refresh
        </Button>
        <Button
          onClick={handleBulkCleanup}
          disabled={actionLoading !== null}
          style={{ backgroundColor: "#ef444418", borderColor: "#ef444444", color: "#ef4444" }}
        >
          {actionLoading === "bulk_cleanup_history" ? "Cleaning..." : "Bulk Cleanup History"}
        </Button>
      </div>

      {error && (
        <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12,
            backgroundColor: "#111827",
            borderRadius: 6,
            overflow: "hidden",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#1f2937", textAlign: "left" }}>
              <th style={thStyle}>Symbol</th>
              <th style={thStyle}>Account</th>
              <th style={thStyle}>Redis</th>
              <th style={thStyle}>UserPos</th>
              <th style={thStyle}>TradeLog</th>
              <th style={thStyle}>Queue</th>
              <th style={thStyle}>Issues</th>
              <th style={thStyle}>Severity</th>
              <th style={thStyle}>Suggested Fix</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && data.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ ...tdStyle, textAlign: "center", color: "#6b7280", padding: 24 }}>
                  Loading...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ ...tdStyle, textAlign: "center", color: "#6b7280", padding: 24 }}>
                  No diagnostics rows found
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr
                  key={`${row.symbol}-${row.account_id}-${idx}`}
                  style={{
                    borderBottom: "1px solid #1f2937",
                    backgroundColor:
                      row.severity === "critical"
                        ? "#ef444408"
                        : row.severity === "warning"
                          ? "#f59e0b08"
                          : "transparent",
                  }}
                >
                  <td style={tdStyle}>
                    <strong>{row.symbol}</strong>
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.account_id.slice(0, 12)}...
                  </td>
                  <td style={tdStyle}>
                    <SourceDot exists={row.redis.exists} />
                  </td>
                  <td style={tdStyle}>
                    <SourceDot exists={row.user_position.exists} />
                  </td>
                  <td style={tdStyle}>
                    <SourceDot exists={row.trade_log.row_count > 0} />
                    {row.trade_log.row_count > 1 && (
                      <span style={{ color: "#f59e0b", marginLeft: 4 }}>
                        x{row.trade_log.row_count}
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {row.queue_gate.blocked ? (
                      <span style={{ color: "#ef4444", fontWeight: 600 }}>Blocked</span>
                    ) : (
                      <span style={{ color: "#6b7280" }}>Open</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {row.issues.length === 0 ? (
                      <span style={{ color: "#6b7280" }}>—</span>
                    ) : (
                      row.issues.map((issue: any, i: number) => (
                        <IssueBadge key={i} label={issue.label} severity={issue.severity} />
                      ))
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        color: SEVERITY_COLORS[row.severity as Severity] || "#6b7280",
                        fontWeight: 600,
                      }}
                    >
                      {SEVERITY_LABELS[row.severity as Severity] || row.severity}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {row.suggested_fixes.length === 0 ? (
                      <span style={{ color: "#6b7280" }}>—</span>
                    ) : (
                      row.suggested_fixes.map((fix: string, i: number) => (
                        <span key={i} style={{ color: "#3b82f6", fontSize: 11 }}>
                          {ACTION_LABELS[fix] || fix}
                          {i < row.suggested_fixes.length - 1 ? ", " : ""}
                        </span>
                      ))
                    )}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        onClick={() => handleInspect(row)}
                        style={smallBtnStyle}
                      >
                        Inspect
                      </button>
                      {row.suggested_fixes.slice(0, 2).map((fix: string) => (
                        <button
                          key={fix}
                          onClick={() => handleAction(row, fix)}
                          disabled={actionLoading === fix}
                          style={{
                            ...smallBtnStyle,
                            backgroundColor: "#3b82f618",
                            color: "#3b82f6",
                            borderColor: "#3b82f644",
                          }}
                        >
                          {actionLoading === fix
                            ? "..."
                            : ACTION_LABELS[fix] || fix}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {detailModal && (
        <DetailModalPanel
          detail={detailModal}
          onClose={() => setDetailModal(null)}
        />
      )}
    </div>
  );
}

function CounterBox({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color: string;
}) {
  return (
    <div
      style={{
        padding: "10px 16px",
        borderRadius: 8,
        backgroundColor: "#1f2937",
        border: `1px solid ${color}33`,
        minWidth: 100,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{label}</div>
    </div>
  );
}

function SourceDot({ exists }: { exists: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: exists ? "#10b981" : "#374151",
      }}
    />
  );
}

function DetailModalPanel({
  detail,
  onClose,
}: {
  detail: DiagnosticsDetailResponse;
  onClose: () => void;
}) {
  const diagnostics = detail.diagnostics as Record<string, any>;
  const issues = diagnostics?.issues || [];
  const severity = diagnostics?.severity || "normal";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#111827",
          borderRadius: 10,
          maxWidth: 700,
          width: "95%",
          maxHeight: "85vh",
          overflow: "auto",
          padding: 20,
          border: "1px solid #374151",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            {detail.symbol} / {detail.account_id.slice(0, 12)}...
          </h3>
          <Button onClick={onClose}>Close</Button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <span
            style={{
              color: SEVERITY_COLORS[severity as Severity] || "#6b7280",
              fontWeight: 600,
            }}
          >
            {SEVERITY_LABELS[severity as Severity] || severity} — {issues.length} issue(s)
          </span>
        </div>

        {issues.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {issues.map((issue: any, i: number) => (
              <div key={i} style={{ marginBottom: 6 }}>
                <IssueBadge label={issue.label} severity={issue.severity} />
                <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: 6 }}>
                  {issue.description}
                </span>
              </div>
            ))}
          </div>
        )}

        {detail.block_reason && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              backgroundColor: "#ef444416",
              border: "1px solid #ef444433",
              color: "#ef4444",
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {detail.block_reason}
          </div>
        )}

        <details style={{ marginBottom: 12 }}>
          <summary style={{ cursor: "pointer", color: "#60a5fa", fontSize: 13 }}>
            Redis Raw Data
          </summary>
          <pre style={rawPreStyle}>
            {detail.redis_raw
              ? JSON.stringify(detail.redis_raw, null, 2)
              : "No Redis data"}
          </pre>
        </details>

        <details style={{ marginBottom: 12 }}>
          <summary style={{ cursor: "pointer", color: "#60a5fa", fontSize: 13 }}>
            UserPosition Raw Data
          </summary>
          <pre style={rawPreStyle}>
            {detail.user_position_raw
              ? JSON.stringify(detail.user_position_raw, null, 2)
              : "No UserPosition data"}
          </pre>
        </details>

        <details style={{ marginBottom: 12 }}>
          <summary style={{ cursor: "pointer", color: "#60a5fa", fontSize: 13 }}>
            TradeLog Raw Data
          </summary>
          <pre style={rawPreStyle}>
            {detail.trade_log_raw || "No TradeLog data"}
          </pre>
        </details>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: 11,
  fontWeight: 600,
  color: "#9ca3af",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "7px 10px",
  color: "#d1d5db",
  verticalAlign: "middle",
};

const rawPreStyle: React.CSSProperties = {
  backgroundColor: "#1f2937",
  color: "#d1d5db",
  padding: 10,
  borderRadius: 6,
  fontSize: 11,
  overflowX: "auto",
  maxHeight: 250,
  margin: 0,
  whiteSpace: "pre-wrap",
};

const smallBtnStyle: React.CSSProperties = {
  padding: "2px 8px",
  fontSize: 11,
  borderRadius: 4,
  border: "1px solid #374151",
  backgroundColor: "#1f2937",
  color: "#d1d5db",
  cursor: "pointer",
};
